/**
 * Status bar — the always-visible answer to "can I run right now?".
 *
 * It polls `get_system_status` on a 15s cadence (the backend caches for 10s, so
 * this costs at most one probe per interval) and paints each fact with a dot:
 * green = usable, amber = degraded but usable, red = blocked.
 */

import * as React from "react";
import { Bell, Container, Cpu, HardDrive, Package, User } from "lucide-react";

import { StatusDot } from "@/components/ui/primitives";
import { formatBytes } from "@/lib/utils";
import {
  useAppStore,
  useCurrentCase,
  useCurrentScan,
  useJob,
  useNotifications,
  useSelectedNodeId,
  useSystemStatus,
  useSystemStatusError,
} from "@/store/appStore";
import { findNode, useViewerStore } from "@/viewer/viewerStore";
import { cn } from "@/lib/utils";

const POLL_MS = 15_000;

type Tone = "neutral" | "blue" | "green" | "amber" | "red";

function Cell({
  icon: Icon,
  tone,
  label,
  value,
  title,
}: {
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  tone?: Tone;
  label?: string;
  value: string;
  title?: string;
}) {
  return (
    <div
      title={title ?? (label ? `${label}: ${value}` : value)}
      className="flex h-full items-center gap-1.5 border-r border-ads-border px-3 whitespace-nowrap"
    >
      {tone ? <StatusDot tone={tone} /> : null}
      {Icon ? <Icon size={13} className="text-ads-muted" /> : null}
      {label ? <span className="text-ads-muted">{label}:</span> : null}
      <span className="text-ads-text">{value}</span>
    </div>
  );
}

/** One short phrase for the status bar; the full sentence is the tooltip. */
/**
 * The short form of `deviceLabel`, for a status bar cell.
 *
 * `deviceClass` alone cannot say this: Metal is `cpu` there, because only the
 * two torch stages move to the GPU and calling the whole engine a GPU engine
 * would overstate it. So the label — which is where the backend spells out
 * what it actually does — decides, and this cell and the engine pill on Home
 * agree instead of one saying CPU while the other says Metal.
 */
function engineShort(engine: {
  kind: string;
  ready: boolean;
  deviceClass: string;
  deviceLabel?: string;
}): string {
  if (!engine.ready) return "Not set up";
  const where = engine.kind === "native" ? "Local" : "Docker";
  const on =
    engine.deviceClass === "cuda"
      ? "CUDA"
      : engine.deviceClass === "emulated"
        ? "emulated"
        : engine.deviceLabel?.includes("Metal —")
          ? "Metal"
          : "CPU";
  return `${where} · ${on}`;
}

export function StatusBar() {
  const status = useSystemStatus();
  const statusError = useSystemStatusError();
  const refresh = useAppStore((s) => s.refreshSystemStatus);
  const currentCase = useCurrentCase();
  const scan = useCurrentScan();
  const job = useJob();
  const selectedNodeId = useSelectedNodeId();
  const baseNodeId = useViewerStore((s) => s.baseNodeId);
  const spaceLabel =
    findNode(currentCase, selectedNodeId)?.space ?? findNode(currentCase, baseNodeId)?.space ?? "native";
  const notifications = useNotifications();
  const markRead = useAppStore((s) => s.markNotificationsRead);
  const [bellOpen, setBellOpen] = React.useState(false);

  React.useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(true), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const docker = status?.docker;
  const engine = status?.engine;
  const gpu = status?.gpu;
  const storage = status?.storage?.[0];
  const unread = notifications.filter((n) => !n.read).length;

  // A run that has gone quiet says why, here, because this line is on every
  // page and the run view is not. "Waiting on memory" is the one state worth
  // colouring differently from "working".
  const health = job?.state === "running" ? job.health : null;

  const overall: { tone: Tone; text: string } = job?.state === "running"
    ? {
        tone: health?.paging ? "amber" : "blue",
        text: `Running (${Math.round(job.progressPct)}%)`,
      }
    : job?.state === "failed"
      ? { tone: "red", text: "Failed" }
      : statusError
        ? { tone: "red", text: "Status unavailable" }
        : engine?.ready
          ? { tone: "green", text: "Ready" }
          : { tone: "amber", text: "No engine" };

  // "emulated" is a working engine, just a slow one, so it reads amber rather
  // than green or red.
  const engineTone: Tone = !engine
    ? "neutral"
    : !engine.ready
      ? "red"
      : engine.deviceClass === "emulated"
        ? "amber"
        : "green";
  const gpuTone: Tone = !gpu ? "neutral" : gpu.available ? "green" : "amber";

  return (
    <footer className="relative flex h-7 shrink-0 items-stretch border-t border-ads-border bg-ads-surface text-[11.5px]">
      <Cell tone={overall.tone} value={overall.text} title={statusError ?? undefined} />
      {health ? (
        <Cell
          tone={health.paging ? "amber" : "neutral"}
          value={
            health.paging
              ? "Waiting on memory"
              : health.cpuPercent != null
                ? `${(health.cpuPercent / 100).toFixed(1)} cores busy`
                : `Quiet ${health.quietSeconds}s`
          }
          title={health.note}
        />
      ) : null}
      <Cell
        icon={Container}
        tone={engineTone}
        label="Engine"
        value={engine ? engineShort(engine) : "…"}
        title={engine?.deviceLabel ?? engine?.error ?? undefined}
      />
      {/* GPU is a Docker/CUDA concern; with the native engine on this platform
          the device is already in the Engine cell and repeating "CPU only"
          beside it just reads as a fault. */}
      {engine?.kind === "docker" ? (
        <Cell
          icon={Cpu}
          tone={gpuTone}
          label="GPU"
          value={gpu?.devices[0]?.name ?? (gpu?.available === false ? "CPU only" : "…")}
          title={gpu?.error ?? undefined}
        />
      ) : null}
      {engine?.kind === "docker" ? (
        <Cell icon={Package} label="Image" value={docker?.selectedImage ?? "—"} />
      ) : null}
      <Cell icon={User} label="Subject" value={currentCase?.subjectId ?? scan?.subjectId ?? "—"} />
      <Cell label="Space" value={spaceLabel} />

      <div className="flex-1" />

      <Cell
        icon={HardDrive}
        label="Storage"
        value={storage ? `${formatBytes(storage.freeBytes)} free` : "—"}
        title={storage?.path}
      />
      <Cell value={status ? `v${status.appVersion}` : "—"} />

      <button
        type="button"
        aria-label="Notifications"
        title={`${notifications.length} notifications`}
        onClick={() => {
          setBellOpen((v) => !v);
          if (!bellOpen) markRead();
        }}
        className="relative flex w-9 items-center justify-center hover:bg-ads-hover"
      >
        <Bell size={14} className="text-ads-muted" />
        {unread > 0 ? (
          <span className="absolute top-0.5 right-1 flex h-3 min-w-3 items-center justify-center rounded-full bg-ads-accent px-[3px] text-[9px] leading-none font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </button>

      {bellOpen ? (
        <div className="absolute right-1 bottom-8 z-50 max-h-72 w-80 overflow-auto rounded border border-ads-border-strong bg-ads-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-ads-border px-3 py-1.5">
            <span className="text-[12px] font-semibold text-ads-text">Notifications</span>
            <button
              type="button"
              className="text-[11px] text-ads-accent hover:underline"
              onClick={() => useAppStore.getState().clearNotifications()}
            >
              Clear
            </button>
          </div>
          {notifications.length === 0 ? (
            <p className="px-3 py-4 text-center text-[11.5px] text-ads-muted">Nothing to report.</p>
          ) : (
            notifications.map((n) => (
              <div key={n.id} className="border-b border-ads-border px-3 py-2 last:border-b-0">
                <div className="flex items-center gap-2">
                  <StatusDot
                    tone={
                      n.tone === "error"
                        ? "red"
                        : n.tone === "warning"
                          ? "amber"
                          : n.tone === "success"
                            ? "green"
                            : "blue"
                    }
                  />
                  <span className={cn("text-[12px] text-ads-text")}>{n.title}</span>
                </div>
                {n.detail ? <p className="mt-0.5 pl-4 text-[11px] text-ads-muted">{n.detail}</p> : null}
              </div>
            ))
          )}
        </div>
      ) : null}
    </footer>
  );
}
